import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Window from "../common/Window";
import Chip, { ChipRow } from "../common/Chip";
import PointList from "../common/PointList";
import { stampIn, staggerContainer, viewport } from "../../utils/motion";

// One campsite dossier, rendered in dell-1996 ribbon-card chrome on the tan
// Experience biome.
//
// An entry carries EITHER a `detail` paragraph (the employment ones) or a
// `points` list (the self-directed one, where each item is a discrete result
// worth its own line and its own link).

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Logo = styled.div`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 0;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 3px;
  }
`;

const Head = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const Company = styled.span`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 17px;
  font-weight: 900;
  color: ${({ theme }) => theme.biome.experience.ink};
`;

const Current = styled.em`
  font-style: italic;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.biome.experience.accent};
  margin-left: 7px;
`;

const Role = styled.h3`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.biome.experience.ink};
  margin: 5px 0 1px;
`;

const Dates = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 13px;
  color: ${({ theme }) => theme.biome.experience.muted};
`;

const Location = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 12.5px;
  color: ${({ theme }) => theme.biome.experience.muted};
  opacity: 0.85;
`;

const Detail = styled.p`
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.biome.experience.muted};
  margin: 12px 0 0;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.18);

  /* Justified, with hyphenation on so the long words in these paragraphs
     don't open rivers of whitespace between the flush edges. */
  text-align: justify;
  hyphens: auto;
`;

const ExperienceCard = ({ experience }) => (
  <Window label={experience.company} tone="experience" pad="16px">
      <Top>
        {/* Not every entry has a company logo — the self-directed one doesn't,
            and an empty <img> would render as a broken-image glyph. */}
        {experience.img && (
          <Logo>
            <img src={experience.img} alt={experience.company} loading="lazy" />
          </Logo>
        )}
        <Head>
          <CompanyRow>
            <span>
              <Company>{experience.company}</Company>
              {experience.current && <Current>current</Current>}
            </span>
          </CompanyRow>
          {experience.role && <Role>{experience.role}</Role>}
          <Dates>
            {experience.date}
            {experience.employment && ` · ${experience.employment}`}
          </Dates>
          {experience.location && <Location>{experience.location}</Location>}
        </Head>
      </Top>

      {experience.detail && <Detail>{experience.detail}</Detail>}

      <PointList points={experience.points} />

      {experience.skills?.length > 0 && (
        <ChipRow
          as={motion.div}
          style={{ marginTop: 14 }}
          variants={staggerContainer(0.035)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
        >
          {experience.skills.map((skill) => (
            <Chip
              key={skill}
              as={motion.span}
              variants={stampIn}
              $tone="experience"
            >
              {skill}
            </Chip>
          ))}
        </ChipRow>
      )}
  </Window>
);

export default ExperienceCard;
